import { Person, PersonCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useCreatePerson, usePersons } from '@/hooks/api-hooks';
import { Button, Loader, TextInput, NumberInput, ActionIcon, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconId, IconMail, IconPhone, IconPhonePlus, IconX, IconUserPlus, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { TagIcon } from '@/lib/tag-utils';
import { notify } from '@/lib/notify';
import { PaxCategoryBadge, ShowPaxGroup } from './snippets';

const MIN_CHARS_TO_SEARCH = 2;

interface Props {
    onCreate?: (pax: Person) => void;
    onSelect?: (pax: Person) => void;
    onClose?: () => void;
    preloadName?: string;
    statusBarLeft?: React.ReactNode;
    statusBarRight?: React.ReactNode;
}

export default function CreateOrSelectPerson({
    onCreate,
    onSelect,
    preloadName,
    statusBarLeft,
    statusBarRight,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [name, setName] = useState(preloadName || '');
    const [activeSearchValue, setActiveSearchValue] = useState(preloadName || '');

    const [category, setCategory] = useState<PersonCategory | null>('pax');
    const [adults, setAdults] = useState<number | string>('');
    const [children, setChildren] = useState<number | string>('');
    const [infants, setInfants] = useState<number | string>('');
    const [seniors, setSeniors] = useState<number | string>('');
    const [guests, setGuests] = useState<number | string>('');
    const [email, setEmail] = useState('');
    const [showEmail, setShowEmail] = useState(false);
    const [phone, setPhone] = useState('');
    const [phone_2, setPhone2] = useState('');
    const [showPhone2, setShowPhone2] = useState(false);
    const [document_number, setDocument] = useState('');
    const [showDoc, setShowDoc] = useState(false);
    const [observations, setObservations] = useState('');

    const [showMatches, setShowMatches] = useState<boolean>(false);

    const nameInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const phone2InputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);

    const getSearchQuery = () => {
        if (activeSearchValue.trim().length >= MIN_CHARS_TO_SEARCH) return activeSearchValue.trim();
        if (name.trim().length >= MIN_CHARS_TO_SEARCH) return name.trim();
        if (document_number.trim().length >= MIN_CHARS_TO_SEARCH) return document_number.trim();
        if (phone.trim().length >= MIN_CHARS_TO_SEARCH) return phone.trim();
        if (phone_2.trim().length >= MIN_CHARS_TO_SEARCH) return phone_2.trim();
        if (email.trim().length >= MIN_CHARS_TO_SEARCH) return email.trim();
        return '';
    };

    const searchQuery = getSearchQuery();
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 250);

    const {
        data: searchResponse,
        isLoading: isSearching
    } = usePersons({
        query: debouncedSearchQuery,
    }, {
        enabled: debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH
    });

    const matchingPaxs: Person[] = (searchResponse as any)?.rows || (searchResponse as any)?.data || [];

    const createPersonMutation = useCreatePerson();

    const resetForm = () => {
        setName('');
        setCategory('pax');
        setAdults('');
        setChildren('');
        setInfants('');
        setSeniors('');
        setGuests('');
        setEmail('');
        setShowEmail(false);
        setPhone('');
        setPhone2('');
        setShowPhone2(false);
        setDocument('');
        setShowDoc(false);
        setObservations('');
        setActiveSearchValue('');
        setShowMatches(false);
        setTimeout(() => nameInputRef.current?.focus(), 0);
    };

    const handleSelectPax = (selectedPax: Person) => {
        onCreate?.(selectedPax);
        onSelect?.(selectedPax);
        resetForm();
    };

    const save = () => {
        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length < MIN_CHARS_TO_SEARCH || createPersonMutation.isPending) return;

        createPersonMutation.mutate({
            name: trimmedName,
            email: email.trim() || undefined,
            category: category || undefined,
            adults: Number(adults) || 0,
            children: Number(children) || 0,
            infants: Number(infants) || 0,
            seniors: Number(seniors) || 0,
            guests: Number(guests) || 0,
            phone: phone.trim() || undefined,
            phone_2: phone_2.trim() || undefined,
            document_number: document_number.trim() || undefined,
            observations: observations.trim() || undefined,
        }, {
            onSuccess: (result) => {
                handleSelectPax(result.data);
            },
            onError: (err: any) => {
                const errorKey = err?.response?.data?.error || err?.error || err?.message || 'error';
                notify.error(t(errorKey), t('error'));
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && showMatches) {
            e.preventDefault();
            setShowMatches(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            save();
        }
    };

    useEffect(() => {
        setTimeout(() => nameInputRef.current?.focus(), 0);
    }, []);

    useEffect(() => {
        if (preloadName) {
            setName(preloadName);
            setActiveSearchValue(preloadName);
        }
    }, [preloadName]);

    const canCreateNewPax = name.trim().length >= MIN_CHARS_TO_SEARCH;
    const isActivelySearching = debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH;

    return (
        <div className="w-full max-w-full flex flex-col gap-2">
            <div className="flex flex-col gap-2">
                {/* Row 1: Name + Headcounts */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 w-full">
                    <TextInput
                        data-autofocus
                        autoFocus
                        ref={nameInputRef}
                        size="xs"
                        placeholder={t('name')}
                        value={name}
                        disabled={createPersonMutation.isPending}
                        leftSection={<IconUserPlus size={14} stroke={1.5} className="text-slate-400" />}
                        onChange={(e) => {
                            const val = e.target.value;
                            setName(val);
                            setActiveSearchValue(val);
                        }}
                        onKeyDown={handleKeyDown}
                        className="flex-1 min-w-[280px] font-medium"
                    />
                    <div className="flex items-center gap-1 shrink-0">
                        {([
                            { key: 'adults', val: adults, setter: setAdults },
                            { key: 'children', val: children, setter: setChildren },
                            { key: 'seniors', val: seniors, setter: setSeniors },
                            { key: 'infants', val: infants, setter: setInfants },
                            { key: 'guests', val: guests, setter: setGuests },
                        ] as const).map(({ key, val, setter }) => (
                            <Tooltip key={key} label={t(key)} position="top" withArrow openDelay={200}>
                                <div className="w-[72px]">
                                    <NumberInput
                                        size="xs"
                                        min={0}
                                        hideControls
                                        leftSection={<PaxCategoryBadge category={key} />}
                                        leftSectionWidth={24}
                                        value={val}
                                        disabled={createPersonMutation.isPending}
                                        onChange={(v) => setter(v)}
                                        onKeyDown={handleKeyDown}
                                        className="font-semibold"
                                        variant='filled'
                                        styles={{
                                            input: {
                                                paddingLeft: 22,
                                                paddingRight: 2,
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                {/* Row 2: Phone + Expandable Phone 2, Document, Email + Save */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5">
                    <div className="flex items-center gap-1 flex-1 min-w-[140px]">
                        <TextInput
                            ref={phoneInputRef}
                            size="xs"
                            type="tel"
                            variant='filled'
                            placeholder={t('phone')}
                            leftSection={<IconPhone size={13} stroke={1.5} className="text-slate-400" />}
                            value={phone}
                            disabled={createPersonMutation.isPending}
                            onChange={(e) => {
                                const val = e.target.value;
                                setPhone(val);
                                setActiveSearchValue(val);
                            }}
                            onKeyDown={handleKeyDown}
                            className="flex-1 min-w-0"
                        />
                        {!showPhone2 && (
                            <Tooltip label={t('add_second_phone')} withArrow>
                                <ActionIcon
                                    size={28}
                                    radius="sm"
                                    variant="subtle"
                                    color="gray"
                                    className="shrink-0 text-slate-500"
                                    onClick={() => {
                                        setShowPhone2(true);
                                        setTimeout(() => phone2InputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconPhonePlus size={14} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {!showDoc && (
                            <Tooltip label={t('id_number')} withArrow>
                                <ActionIcon
                                    size={28}
                                    radius="sm"
                                    variant="subtle"
                                    color="gray"
                                    className="shrink-0 text-slate-500"
                                    onClick={() => {
                                        setShowDoc(true);
                                        setTimeout(() => docInputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconId size={14} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        {!showEmail && (
                            <Tooltip label={t('email')} withArrow>
                                <ActionIcon
                                    size={28}
                                    radius="sm"
                                    variant="subtle"
                                    color="gray"
                                    className="shrink-0 text-slate-500"
                                    onClick={() => {
                                        setShowEmail(true);
                                        setTimeout(() => emailInputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconMail size={14} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </div>

                    {showPhone2 && (
                        <div className="flex items-center gap-1 flex-1 min-w-[115px]">
                            <TextInput
                                ref={phone2InputRef}
                                size="xs"
                                type="tel"
                                placeholder={t('phone_2')}
                                leftSection={<IconPhone size={13} className="text-slate-400" />}
                                value={phone_2}
                                disabled={createPersonMutation.isPending}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setPhone2(val);
                                    setActiveSearchValue(val);
                                }}
                                onKeyDown={handleKeyDown}
                                className="flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={26}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setPhone2('');
                                    setShowPhone2(false);
                                }}
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        </div>
                    )}

                    {showDoc && (
                        <div className="flex items-center gap-1 flex-1 min-w-[115px]">
                            <TextInput
                                ref={docInputRef}
                                size="xs"
                                placeholder={t('id_number')}
                                leftSection={<IconId size={13} className="text-slate-400" />}
                                value={document_number}
                                disabled={createPersonMutation.isPending}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setDocument(val);
                                    setActiveSearchValue(val);
                                }}
                                onKeyDown={handleKeyDown}
                                className="flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={26}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setDocument('');
                                    setShowDoc(false);
                                }}
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        </div>
                    )}

                    {showEmail && (
                        <div className="flex items-center gap-1 flex-1 min-w-[125px]">
                            <TextInput
                                ref={emailInputRef}
                                size="xs"
                                type="email"
                                placeholder={t('email')}
                                leftSection={<IconMail size={13} className="text-slate-400" />}
                                value={email}
                                disabled={createPersonMutation.isPending}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setEmail(val);
                                    setActiveSearchValue(val);
                                }}
                                onKeyDown={handleKeyDown}
                                className="flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={26}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setEmail('');
                                    setShowEmail(false);
                                }}
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        </div>
                    )}

                    <Button
                        onClick={save}
                        loading={createPersonMutation.isPending}
                        disabled={!canCreateNewPax}
                        size="xs"
                        radius="md"
                        color="dark"
                        leftSection={<IconUserPlus size={13} />}
                        className="shrink-0 ml-auto"
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>

            {/* Status Row (Pax List header + Inline Search Status) & Collapsible Matched Paxs List */}
            {(Boolean(statusBarLeft || statusBarRight) || (isActivelySearching && (isSearching || matchingPaxs.length > 0))) && (
                <div className="flex flex-col gap-1 pt-1">
                    <div className="flex items-center justify-between gap-2 min-h-[22px] px-0.5">
                        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
                            {statusBarLeft}

                            {isActivelySearching && (
                                matchingPaxs.length > 0 ? (
                                    <div
                                        onClick={() => setShowMatches((prev) => !prev)}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-400 hover:text-blue-800 transition-colors cursor-pointer select-none"
                                    >
                                        {isSearching ? (
                                            <Loader size={11} color="blue" />
                                        ) : showMatches ? (
                                            <IconChevronDown size={12} stroke={1.75} />
                                        ) : (
                                            <IconChevronRight size={12} stroke={1.75} />
                                        )}
                                        <span>{t('found_matching_paxs', { count: matchingPaxs.length })}</span>
                                    </div>
                                ) : isSearching ? (
                                    <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                                        <Loader size={11} color="gray" />
                                    </div>
                                ) : null
                            )}
                        </div>

                        {statusBarRight}
                    </div>

                    {isActivelySearching && showMatches && matchingPaxs.length > 0 && (
                        <div className="max-h-[160px] overflow-y-auto subtle-scrollbar divide-y divide-[#f1f1ef] border border-[#f1f1ef] rounded-md bg-white">
                            {matchingPaxs.map((pax) => {
                                const hasDoc = !!(pax.document_type || pax.document_number);
                                const hasMail = !!pax.email;
                                const hasPhone = !!pax.phone;
                                const docStr = [pax.document_type, pax.document_number].filter(Boolean).join(' ');
                                const hasPaxCounts =
                                    (pax.adults || 0) +
                                    (pax.children || 0) +
                                    (pax.seniors || 0) +
                                    (pax.infants || 0) +
                                    (pax.guests || 0) > 0;

                                return (
                                    <div
                                        key={pax.id}
                                        className="px-2 py-1.5 rounded-sm transition-colors cursor-pointer flex items-center justify-between gap-2 group hover:bg-[#f7f7f5]"
                                        onClick={() => handleSelectPax(pax)}
                                    >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {pax.category && <TagIcon name={pax.category} hideText size="xxs" noBackground />}
                                            <span className="font-medium text-xs text-slate-800 truncate">
                                                {pax.name}
                                            </span>
                                            {hasPaxCounts && (
                                                <ShowPaxGroup
                                                    size="xs"
                                                    adults={pax.adults}
                                                    children={pax.children}
                                                    seniors={pax.seniors}
                                                    infants={pax.infants}
                                                    guests={pax.guests}
                                                />
                                            )}

                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-400">
                                                {hasDoc && (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <IconId size={10} className="shrink-0" />
                                                        {docStr}
                                                    </span>
                                                )}
                                                {hasPhone && (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <IconPhone size={10} className="shrink-0" />
                                                        {pax.phone}
                                                    </span>
                                                )}
                                                {hasMail && (
                                                    <span className="inline-flex items-center gap-0.5 truncate max-w-[140px]">
                                                        <IconMail size={10} className="shrink-0" />
                                                        <span className="truncate">{pax.email}</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors shrink-0 inline-flex items-center gap-1 text-slate-400 group-hover:text-slate-700">
                                            <span>{t('select_and_add')}</span>
                                            <kbd className="font-mono text-[9px]">↵</kbd>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
